import { z } from "zod";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { Button } from "../../components/ui/Button";
import { displayToast } from "../../components/app/AppToast";
import { gaEventVisualAbstractReportSubmitted } from "../../tools/analytics/domains/article.analytics";
import { apiClient } from "../../services/api";

const reportFormSchema = z.object({
  category: z.enum([
    "incorrect_number",
    "unsupported_claim",
    "attribution_or_license",
    "patient_privacy",
    "other",
  ]),
  message: z.string().trim().max(1_000),
});

export default function VisualAbstractReportPage({
  id,
}: {
  id: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();

  async function submitReport(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parsed = reportFormSchema.parse({
      category: form.get("category"),
      message: form.get("message"),
    });
    try {
      const response = await apiClient.post(
        "/webapp/private/visual-abstracts/:id/reports",
        {
          param: { id },
          json: {
            category: parsed.category,
            message: parsed.message === "" ? null : parsed.message,
          },
        }
      );
      // The route names the ARTIFACT, so the id is safe to read straight from
      // it — that is the whole point of this screen no longer being addressed
      // by the share token. Only `sourceType` has to come back from the server:
      // this page never loads the poster.
      gaEventVisualAbstractReportSubmitted({
        visualAbstractId: id,
        sourceType: response.body.sourceType,
        category: parsed.category,
      });
      displayToast(t("Thank you. Your report has been sent."), "success");
      history.goBack();
    } catch (error: unknown) {
      displayToast(
        error instanceof Error
          ? error.message
          : t("Unable to send your report"),
        "error"
      );
    }
  }

  return (
    <ScrollableLayout
      background="var(--color-ds-cream)"
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent title={t("Report an inaccuracy")} />}
    >
      <main className="mx-auto flex w-full max-w-xl flex-col gap-4 p-5">
        <div className="flex flex-col gap-2">
          <h1 className="m-0 font-dm-sans text-title font-semibold text-ds-mint-900">
            {t("Report an inaccuracy")}
          </h1>
          <p className="m-0 text-body text-ds-slate-600">
            {t(
              "Tell us what needs review. Your report is linked to this visual abstract and handled by the Juisci team."
            )}
          </p>
        </div>

        <form
          className="flex flex-col gap-4 rounded-2xl border border-ds-border-btn bg-white p-4"
          onSubmit={(event) => {
            void submitReport(event);
          }}
        >
          <label className="flex flex-col gap-2 text-footnote font-semibold text-ds-mint-900">
            {t("Issue type")}
            <select
              className="h-12 rounded-xl border border-ds-border-btn bg-white px-3 font-normal text-ds-mint-900"
              name="category"
              defaultValue="incorrect_number"
            >
              <option value="incorrect_number">{t("Incorrect number")}</option>
              <option value="unsupported_claim">
                {t("Unsupported claim")}
              </option>
              <option value="attribution_or_license">
                {t("Attribution or licence")}
              </option>
              <option value="patient_privacy">{t("Patient privacy")}</option>
              <option value="other">{t("Other")}</option>
            </select>
          </label>

          <label className="flex flex-col gap-2 text-footnote font-semibold text-ds-mint-900">
            {t("Details (optional)")}
            <textarea
              className="min-h-32 resize-y rounded-xl border border-ds-border-btn bg-white p-3 font-normal text-ds-mint-900"
              name="message"
              maxLength={1_000}
            />
          </label>

          <Button type="submit" variant="primary">
            {t("Send report")}
          </Button>
        </form>
      </main>
    </ScrollableLayout>
  );
}
